import { detach, updateCSSText } from '@syncfusion/ej2-base';
import { BlockType } from '../../models/enums';
import { detectFormatsForTextNode, getBlockSpecificRange, getNormalizedKey, getParentBlock, getSelectedRange, hasActiveTableSelection } from '../../common/utils/index';
import { events } from '../../common/constant';
import { findClosestParent, getNodesInRange } from '../../common/utils/dom';
import * as constants from '../../common/constant';
/**
 * InlineToolbarModule class is used to render the inline toolbar for the block editor.
 *
 * @hidden
 */
export class InlineToolbarModule {
    constructor(manager) {
        this.parent = manager;
        this.addEventListeners();
    }
    addEventListeners() {
        this.parent.observer.on(events.inlineToolbarItemClick, this.handleInlineToolbarItemClick, this);
        this.parent.observer.on('toolbarCreated', this.handleToolbarCreated, this);
        this.parent.observer.on('handleColorpickerChange', this.handleColorChange, this);
        this.parent.observer.on('popupWidthChanged', this.handlePopupWidthChanges, this);
        this.parent.observer.on(events.formattingPerformed, this.toggleToolbarActiveState, this);
        this.parent.observer.on(events.keydown, this.onKeydown, this);
        this.parent.observer.on(events.destroy, this.destroy, this);
    }
    removeEventListeners() {
        this.parent.observer.off(events.inlineToolbarItemClick, this.handleInlineToolbarItemClick);
        this.parent.observer.off('toolbarCreated', this.handleToolbarCreated);
        this.parent.observer.off('handleColorpickerChange', this.handleColorChange);
        this.parent.observer.off('popupWidthChanged', this.handlePopupWidthChanges);
        this.parent.observer.off(events.formattingPerformed, this.toggleToolbarActiveState);
        this.parent.observer.off(events.keydown, this.onKeydown);
        this.parent.observer.off(events.destroy, this.destroy);
    }
    handleToolbarCreated() {
        this.toolbarEle = this.parent.rootEditorElement.querySelector('#' + this.parent.rootEditorElement.id + constants.BLOCKEDITOR_INLINETBAR_ID);
        this.init();
    }
    init() {
        this.popupElement = this.parent.rootEditorElement.querySelector('#' + this.parent.rootEditorElement.id + constants.INLINE_TBAR_POPUP_ID);
        this.popupObj = this.parent.popupRenderer.renderPopup({
            element: this.popupElement,
            content: this.toolbarEle
        });
        this.popupObj.actionOnScroll = 'none';
    }
    onKeydown(e) {
        const normalizedKey = getNormalizedKey(e);
        if (!normalizedKey) {
            return;
        }
        const command = this.parent.keyCommandMap.get(normalizedKey);
        const allowedCommands = ['bold', 'italic', 'underline', 'strikethrough', 'inlineCode'];
        const tableBlk = this.parent.currentFocusedBlock &&
            this.parent.currentFocusedBlock.closest(`.${constants.TABLE_BLOCK_CLS}`);
        if (allowedCommands.indexOf(command) !== -1) {
            e.preventDefault();
            if (window.getSelection().isCollapsed && !(tableBlk && hasActiveTableSelection(tableBlk))) {
                this.parent.formattingAction.toggleActiveFormats(command);
            }
            else {
                this.parent.execCommand({ command: 'FormattingAction', state: { command: command } });
            }
        }
    }
    isSelectionInTableHeader(range) {
        // Resolve a reliable HTMLElement anchor from the range
        const startEl = (range.startContainer.nodeType === Node.ELEMENT_NODE
            ? range.startContainer
            : range.startContainer.parentElement);
        if (!startEl) {
            return false;
        }
        // Nearest header cell: either an actual <th> or an element marked as column header
        const headerCell = startEl.closest('th,[role="columnheader"]');
        if (!headerCell) {
            return false;
        }
        // Ensure it's within a THEAD to qualify as header, not body cells styled as header
        const inThead = headerCell.closest('thead');
        return !!inThead;
    }
    /**
     * Shows the inline toolbar at the current selection
     *
     * @param {Range} range - Selection range where the toolbar should appear
     * @param {Event} event - Optional event that triggered the toolbar
     * @returns {void}
     * @hidden
     */
    showInlineToolbar(range, event) {
        const notAllowedTypes = [BlockType.Code];
        const blockElement = findClosestParent(range.startContainer, '.' + constants.BLOCK_CLS);
        const blockType = blockElement.getAttribute('data-block-type');
        if (!this.parent.inlineToolbarSettings.enable || (notAllowedTypes.indexOf(blockType) !== -1)) {
            return;
        }
        if (this.isSelectionInTableHeader(range)) {
            return;
        }
        this.toggleToolbarActiveState();
        this.parent.popupRenderer.adjustPopupPositionRelativeToTarget(range, this.popupObj);
        this.popupObj.show();
    }
    /**
     * Hides the inline toolbar
     *
     * @param {Event} e - Optional event that triggered hiding the toolbar
     * @returns {void}
     */
    hideInlineToolbar(e) {
        if (this.popupElement && !this.popupElement.classList.contains('e-popup-open')) {
            return;
        }
        this.popupObj.hide();
    }
    handleInlineToolbarItemClick(args) {
        const selectedItem = args.item.id;
        if (selectedItem === 'color' || selectedItem === 'bgColor') {
            // Do not proceed further for color and background color commands. It will be handled by the color picker.
            return;
        }
        if (selectedItem === 'link') {
            // Show link popup when link item is clicked in inline toolbar
            this.parent.linkModule.showLinkPopup(args.originalEvent);
            return;
        }
        this.parent.execCommand({ command: 'FormattingAction', state: { command: selectedItem } });
    }
    /**
     * Updates active state of toolbar buttons based on current selection formatting.
     *
     * @returns {void}
     * @hidden
     */
    toggleToolbarActiveState() {
        const range = getSelectedRange();
        if (!range) {
            return;
        }
        const toolbarItems = this.popupElement.querySelectorAll('.e-toolbar-item');
        toolbarItems.forEach((item) => item.classList.remove('e-active'));
        const blockElements = this.getBlocksInRange(range);
        // Enable/Disable only the 'link' item based on multi-block selection
        const distinctBlocks = blockElements.filter((b) => b.hasAttribute('data-block-type'));
        const enableLink = distinctBlocks.length <= 1;
        if (this.parent && this.parent.editorMethods) {
            this.parent.editorMethods.enableDisableToolbarItems('link', enableLink);
        }
        const detectedFormats = this.detectFormatsFromSelection(range);
        if (!detectedFormats || Object.keys(detectedFormats).length === 0) {
            return;
        }
        for (const item of Array.from(toolbarItems)) {
            const command = item.getAttribute('data-command');
            this.toggleActiveState(item, command, detectedFormats);
        }
    }
    /**
     * Detects active formats from selected text nodes in DOM.
     *
     * @param {Range} range - The selection range
     * @returns {Styles} - Common styles across selection
     * @hidden
     */
    detectFormatsFromSelection(range) {
        const nodesInSelection = [];
        const selectedBlockModels = this.parent.editorMethods.getSelectedBlocks();
        selectedBlockModels.forEach((block) => {
            const blockElement = this.parent.getBlockElementById(block.id);
            const blockRange = getBlockSpecificRange(range, blockElement);
            nodesInSelection.push(...getNodesInRange(blockRange));
        });
        if (nodesInSelection.length === 0) {
            return {};
        }
        const formatsByNode = nodesInSelection.map((textNode) => {
            return detectFormatsForTextNode(textNode);
        });
        return this.getCommonFormatsAcrossNodes(formatsByNode);
    }
    /**
     * Finds common formats across multiple text nodes.
     * A format is "active" if ALL selected text nodes have it.
     *
     * @param {Styles[]} formatsByNode - Format styles for each text node
     * @returns {Styles} - Only formats that all nodes share
     */
    getCommonFormatsAcrossNodes(formatsByNode) {
        if (!formatsByNode.length) {
            return {};
        }
        if (formatsByNode.length === 1) {
            return formatsByNode[0];
        }
        const commonFormats = {};
        const firstNodeFormats = formatsByNode[0];
        for (const format of Object.keys(firstNodeFormats)) {
            const firstValue = firstNodeFormats[format];
            const isCommon = formatsByNode.every((nodeFormats) => {
                const value = nodeFormats[format];
                return value === firstValue;
            });
            if (isCommon) {
                commonFormats[format] = firstValue;
            }
        }
        return commonFormats;
    }
    getBlocksInRange(range) {
        const blocks = [];
        const startBlock = getParentBlock(range.startContainer);
        const endBlock = getParentBlock(range.endContainer);
        if (!startBlock) {
            return blocks;
        }
        // Single block case
        if (startBlock === endBlock) {
            blocks.push(startBlock);
            return blocks;
        }
        // Multi-block case
        let current = startBlock;
        while (current && current !== endBlock) {
            blocks.push(current);
            current = current.nextElementSibling;
        }
        if (endBlock) {
            blocks.push(endBlock);
        }
        return blocks.filter((block) => block.hasAttribute('data-block-type'));
    }
    toggleActiveState(item, command, styles) {
        let isActive = false;
        switch (command) {
            case 'Bold':
                isActive = styles.bold === true;
                break;
            case 'Italic':
                isActive = styles.italic === true;
                break;
            case 'Underline':
                isActive = styles.underline === true;
                break;
            case 'Strikethrough':
                isActive = styles.strikethrough === true;
                break;
            case 'Superscript':
                isActive = styles.superscript === true;
                break;
            case 'Subscript':
                isActive = styles.subscript === true;
                break;
            case 'Uppercase':
                isActive = styles.uppercase === true;
                break;
            case 'Lowercase':
                isActive = styles.lowercase === true;
                break;
            case 'InlineCode':
                isActive = styles.inlineCode === true;
                break;
            case 'Color':
                this.setColors('color', styles.color);
                break;
            case 'BackgroundColor':
                this.setColors('bgColor', styles.backgroundColor);
                break;
        }
        item.classList.toggle('e-active', isActive);
    }
    setColors(type, value) {
        const colorBtn = this.toolbarEle.querySelector('#toolbar-color-dropdown');
        const bgColorBtn = this.toolbarEle.querySelector('#toolbar-bgcolor-dropdown');
        const currentColor = value ? value : '#000000';
        if (currentColor) {
            const button = type === 'color' ?
                colorBtn.querySelector('.e-inline-color-icon') :
                bgColorBtn.querySelector('.e-inline-bgColor-icon');
            if (button) {
                updateCSSText(button, `border-bottom-color: ${currentColor};`);
            }
        }
    }
    handleColorChange(args) {
        const command = args.type === 'bgColor' ? 'backgroundColor' : args.type;
        this.parent.formattingAction.execCommand({
            command: command,
            value: args.value
        });
        this.setColors(args.type, args.value);
    }
    handlePopupWidthChanges(data) {
        this.popupObj.width = this.parent.inlineToolbarSettings.popupWidth = data.value;
    }
    /**
     * Checks whether the slash command popup is opened or not.
     *
     * @returns {boolean} - Returns true if the slash command popup is opened, otherwise false.
     * @hidden
     */
    isPopupOpen() {
        const inlineTlbrPopupElement = document.querySelector('.e-popup.e-blockeditor-inline-toolbar-popup');
        return (inlineTlbrPopupElement.classList.contains('e-popup-open'));
    }
    /**
     * Destroys the inline toolbar module and cleans up resources
     *
     * @returns {void}
     */
    destroy() {
        this.removeEventListeners();
        if (this.popupObj) {
            this.popupObj.destroy();
            detach(this.popupElement);
            this.popupObj = null;
        }
    }
}
